import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

// Marketing routes must always render light - no user toggle, no system preference.
// Authenticated app routes keep their existing dark/light toggle behaviour.
const APP_ROUTE_PREFIXES = ['/app', '/b2b', '/auth', '/admin', '/executor', '/bank', '/settings', '/vault', '/documents', '/crypto', '/estate', '/dashboard'];

function isMarketingPath(pathname) {
  if (!pathname) return true;
  return !APP_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
}

export function ThemeProvider({ children }) {
  const { user, apiCall, accessToken } = useAuth();
  const location = useLocation();
  const marketing = isMarketingPath(location?.pathname);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('safekept_dark_mode');
      if (stored !== null) return stored === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const [initialized, setInitialized] = useState(false);

  // Apply theme to document and localStorage
  useEffect(() => {
    // Marketing routes are always light - never apply dark
    if (marketing) {
      document.documentElement.classList.remove('dark');
      return;
    }
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('safekept_dark_mode', String(darkMode));
  }, [darkMode, marketing]);

  // Sync with user preference from DB when logged in
  useEffect(() => {
    if (user && typeof user.dark_mode === 'boolean' && !initialized) {
      setDarkMode(user.dark_mode);
      setInitialized(true);
    }
  }, [user, initialized]);

  // Listen for system preference changes
  useEffect(() => {
    if (!user) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => setDarkMode(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [user]);

  const toggleDarkMode = useCallback(async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    
    // Persist to DB if logged in
    if (accessToken) {
      try {
        await apiCall('/users/me', {
          method: 'PUT',
          body: { dark_mode: newValue }
        });
      } catch (e) {
        console.error('Failed to save theme preference');
      }
    }
  }, [darkMode, accessToken, apiCall]);

  const value = {
    darkMode,
    toggleDarkMode,
    setDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
